import React, { useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { isMobile } from 'react-device-detect';
import * as Styled from './MainPage.styles';
import Footer from '../../components/Footer';
import OrdersPage from '../OrdersPage';
import ProductsPage from '../ProductsPage';
import withAuth from '../../global/hocs/withAuth';
import Header from '../../components/Header';
import NotificationsList from '../../components/NotificationsList';
import EditPage from '../EditPage';
import OrderPage from '../OrderPage';
import OrderHistoryPage from '../OrderHistoryPage';
import {
  getFromLocalStorage,
  setToLocalStorage,
  getLoyaltyNetworks,
  getLoyaltySources,
} from '../../global/helpers';
import { selectors as selectorsLogin } from '../LoginPage/reducer';
import * as actions from '../../containers/LoginPage/actions';
import { UserRole } from '../OrdersPage/types';
import SourcePage from '../SourcePage';
import SourcesPage from '../SourcesPage';
import PromoCodePage from '../PromoCodePage';
import NetworkPage from '../NetworkPage';
import OperatorsPharmacistPage from '../OperatorsPharmacistPage';
import CreateSourceNetworkPage from '../CreateSourceNetworkPage';
import ReportPage from '../ReportPage';
import TestOrdersPage from '../TestOrdersPage';
import EmptyUserPage from '../EmptyUserPage/EmptyUserPage';
import WebStockPage from '../WebStockPage';
import PharmacyTagsPage from '../PharmacyTagsPage';
import AnalogsPage from '../AnalogsPage';
import { ErrorMessageData } from '../../global/types';
import ErrorAlertModal from '../../components/ErrorAlertModal';
import PharmacistPromoCodePage from '../PharmacistPromoCodePage';
import SettingsPage from '../SettingsPage/SettingsPage';
import SalesPage from '../SalesPage';
import ModerationPage from '../ModerationPage';
import CashbackPage from '../CashbackPage';
import Controls from '../../components/Controls';
import LoyaltyPage from '../LoyaltyPage';
import LoyaltyPageReport from '../LoyaltyPageReport';
import ClientLoyaltyPage from '../ClientLoyaltyPage';
import RXGatePage from '../RXGatePage';
import IntegrationsPage from '../IntegrationsPage';
import SettingsPharmacyPage from '../SettingsPagePharmacy';
import LoyaltyPageSeparated from '../LoyaltyPageSeparated';
import LoyaltyPageV3 from '../LoyaltyPageV3';
import { loyaltyV3TagEnabled } from '../LoyaltyPageV3/coupons/model';
import { isLoyaltyTrainerEnabled } from '../LoyaltyTrainerPage/access';
import SupportWidget from '../../components/SupportWidget';
import { consumeSupportDeepLink } from '../../components/SupportWidget/deepLink';

const LoyaltyTrainerPage = lazy(() => import('../LoyaltyTrainerPage'));

const MainPage = () => {
  const role = getFromLocalStorage('role');
  const dispatch = useDispatch();
  const accessToken = useSelector(selectorsLogin.accessToken);
  const dataUser = useSelector(selectorsLogin.userData);

  const trainerEnabled =
    role === UserRole.PHARMACIST &&
    isLoyaltyTrainerEnabled(dataUser?.networkTags);

  const isLoyaltyTagEnabled = useMemo(
    () =>
      dataUser?.sourceTags?.some((tag: any) =>
        process.env.REACT_APP_DEV_AREA
          ? tag.tag_id === 1043 || tag.tag_id === 1044
          : tag.tag_id === 1154 || tag.tag_id === 1155
      ),
    [dataUser]
  );

  const updClientLoyaltyImg = useMemo(
    () =>
      dataUser?.sourceTags?.some((el: { tag_id: number }) =>
        process.env.REACT_APP_DEV_AREA ? el.tag_id === 1048 : el.tag_id === 1192
      ),
    [dataUser]
  );

  // Новый интерфейс программы лояльности включается только source-тегом.
  // Dev: 1064 без купонов, 1066 с купонами; prod: 1205 и 1202 соответственно.
  const isLoyaltyV3Enabled = useMemo(() => {
    const isDev = process.env.REACT_APP_DEV_AREA === 'true';
    return loyaltyV3TagEnabled(isDev, dataUser?.sourceTags);
  }, [dataUser]);

  // Виджет техподдержки доступен только ФЦ и только если у аптеки есть тег
  // (source-тег на точке или network-тег на сети). dev/prod — по REACT_APP_DEV_AREA.
  const isSupportWidgetEnabled = useMemo(() => {
    const isDev = Boolean(process.env.REACT_APP_DEV_AREA);
    const sourceTagId = isDev ? 1051 : 1193;
    const networkTagId = isDev ? 1052 : 1194;
    return Boolean(
      dataUser?.sourceTags?.some(
        (tag: { tag_id: number }) => tag.tag_id === sourceTagId
      ) ||
        dataUser?.networkTags?.some(
          (tag: { tag_id: number }) => tag.tag_id === networkTagId
        )
    );
  }, [dataUser]);

  // Возвращены теги, но в енвах информация сохранена, на случай необходимости вовзвращения

  // const isLoyaltyTagEnabled = useMemo(
  //   () =>
  //     getLoyaltyNetworks()?.some(
  //       (source: { networkCode: string }) =>
  //         source.networkCode === dataUser?.networkCode
  //     ),
  //   [dataUser?.networkCode]
  // );

  // Переключено на теги, но в енвах информация сохранена, на случай необходимости вовзвращения

  // const isNewLoyaltyFlowEnabled = useMemo(
  //   () =>
  //     process.env.REACT_APP_NEW_LOYALTY_FLOW_SOURCES === 'true' ||
  //     getLoyaltySources()?.some(
  //       (source: { sourceCode: string }) =>
  //         source.sourceCode === dataUser?.sourceCode
  //     ),
  //   [dataUser?.networkCode]
  // );

  const getLoyaltyPage = useMemo(() => {
    if (role === UserRole.MANAGER) {
      return LoyaltyPageReport;
    }
    if (role === UserRole.PHARMACIST && isLoyaltyV3Enabled) {
      return LoyaltyPageV3;
    }
    return LoyaltyPageSeparated;
  }, [role, isLoyaltyV3Enabled]);

  const [errorModalData, setErrorModalData] = useState<ErrorMessageData | null>(
    null
  );
  const [isSupportOpenRequested, setIsSupportOpenRequested] =
    useState<boolean>(false);
  const { t } = useTranslation();

  const history = useHistory();

  const isShowLayout = useMemo(
    () => !history.location.pathname.includes('/user-offline-address-screen'),
    [history]
  );

  useEffect(() => {
    const unlisten = history.listen((location, action) => {
      setErrorModalData(null);
      setToLocalStorage('requestErrorData', null);
      window.dispatchEvent(new Event('storage'));
    });

    return () => {
      unlisten();
    };
  }, [history]);

  useEffect(() => {
    const onConnectionLost = () => {
      setErrorModalData({
        url: '',
        error_message: t('ERROR_MESSAGE_MODAL.POOR_CONNECTION'),
        time: '',
        status: '',
        x_request_id: '',
        x_trace_id: '',
        code: null,
      });
    };

    const checkUserData = (event: StorageEvent) => {
      if (document?.hasFocus() && !event.key && event.key !== 'testKey') {
        const errorModalInfo = getFromLocalStorage('requestErrorData');
        if (
          errorModalInfo &&
          errorModalInfo?.error_message !== 'claim made by yandex'
        ) {
          setErrorModalData(errorModalInfo);
        } else {
          setErrorModalData(null);
        }
      }
    };
    if (
      role === UserRole.ADMIN ||
      role === UserRole.CONTENT ||
      role === UserRole.HEAD_OPERATOR ||
      role === UserRole.OPERATOR ||
      role === UserRole.DISPATCHER_B2B
    ) {
      window.addEventListener('storage', checkUserData);
      window.dispatchEvent(new Event('storage'));
      window.addEventListener('offline', onConnectionLost);

      return () => {
        window.removeEventListener('storage', checkUserData);
        window.removeEventListener('offline', onConnectionLost);
      };
    }
  }, []);

  useEffect(() => {
    if (accessToken && !dataUser) {
      dispatch(actions.getUserInfo.request({ token: accessToken }));
    }
  }, [dispatch, accessToken, dataUser]);

  // Переход по ссылке `/?tickets=true` из WhatsApp: разворачиваем форму тикета
  // сразу после входа. Ждём dataUser — теги для isSupportWidgetEnabled приходят
  // асинхронно, до этого мы не знаем, положен ли аптеке виджет.
  useEffect(() => {
    if (!dataUser) {
      return;
    }
    // Метку снимаем в любом случае, иначе без тега она залипнет до следующего
    // перехода по ссылке и модалка всплывёт при обычном входе.
    if (!consumeSupportDeepLink()) {
      return;
    }
    if (role === UserRole.PHARMACIST && isSupportWidgetEnabled) {
      setIsSupportOpenRequested(true);
    }
  }, [dataUser, role, isSupportWidgetEnabled]);

  return (
    <>
      {isShowLayout && <Header />}
      <Styled.MainPageContentContainer>
        {errorModalData && !isMobile && (
          <ErrorAlertModal errorModalInfo={errorModalData} />
        )}
        <Switch>
          {trainerEnabled && (
            <Route
              exact
              path="/training/loyalty"
              render={() => (
                <Suspense fallback={<p role="status">Загрузка тренажёра…</p>}>
                  <LoyaltyTrainerPage />
                </Suspense>
              )}
            />
          )}
          {(role === UserRole.MANAGER ||
            role === UserRole.PHARMACIST ||
            role === UserRole.PHARMACY_MANAGER) &&
            isLoyaltyTagEnabled &&
            (process.env.REACT_APP_LOYALTY_ENABLED === 'true' ? (
              <Route exact path="/loyalty" component={getLoyaltyPage} />
            ) : (
              <Route
                exact
                path="/loyalty"
                render={() => <Redirect to="/orders" />}
              />
            ))}
          {role === UserRole.PHARMACIST && (
            <Route
              exact
              path="/promo-code"
              component={PharmacistPromoCodePage}
            />
          )}
          {(role === UserRole.ADMIN ||
            role === UserRole.CONTENT ||
            role === UserRole.HEAD_OPERATOR ||
            role === UserRole.OPERATOR) && (
            <Route exact path="/analogs" component={AnalogsPage} />
          )}
          {(role === UserRole.ADMIN ||
            role === UserRole.CONTENT ||
            role === UserRole.HEAD_OPERATOR ||
            role === UserRole.OPERATOR) && (
            <Route exact path="/moderation" component={ModerationPage} />
          )}
          {(role === UserRole.ADMIN || role === UserRole.HEAD_OPERATOR) && (
            <Route exact path="/cashback" component={CashbackPage} />
          )}
          {role === UserRole.CUSTOMER && (
            <Route exact path="/" component={EmptyUserPage} />
          )}
          {role !== UserRole.CUSTOMER && role !== UserRole.CONTENT && (
            <Route exact path="/orders" component={OrdersPage} />
          )}
          {role !== UserRole.CUSTOMER && role !== UserRole.CONTENT && (
            <Route
              exact
              path="/orders/:orderId/history"
              component={OrderHistoryPage}
            />
          )}

          {(role === UserRole.PHARMACIST ||
            role === UserRole.MANAGER ||
            role === UserRole.PHARMACY_MANAGER) && (
            <Route exact path="/web-stock" component={WebStockPage} />
          )}

          {(role === UserRole.PHARMACIST ||
            role === UserRole.MANAGER ||
            role === UserRole.ADMIN ||
            role === UserRole.PHARMACY_MANAGER) && (
            <Route exact path="/sales" component={SalesPage} />
          )}

          {role === UserRole.ADMIN && (
            <>
              <Route exact path="/sources" component={SourcesPage} />
              <Route
                exact
                path="/operators"
                component={OperatorsPharmacistPage}
              />
              <Route exact path="/create" component={CreateSourceNetworkPage} />
              <Route exact path="/sources/:sourceCode" component={SourcePage} />
              <Route exact path="/pharmacy-tags" component={PharmacyTagsPage} />
              <Route exact path="/settings" component={SettingsPage} />
              <Route
                exact
                path="/networks/:networkCode"
                component={NetworkPage}
              />
              <Route exact path="/report" component={ReportPage} />
              <Route exact path="/orders/:orderId" component={OrderPage} />
              <Route exact path="/products" component={ProductsPage} />
              <Route exact path="/edit" component={EditPage} />
              <Route exact path="/promo-code" component={PromoCodePage} />
              {process.env.REACT_APP_INTEGRATIONS_PAGE === 'true' && (
                <Route
                  exact
                  path="/integrations"
                  component={IntegrationsPage}
                />
              )}
            </>
          )}
          {role === UserRole.HEAD_OPERATOR && (
            <>
              <Route exact path="/sources" component={SourcesPage} />
              <Route exact path="/sources/:sourceCode" component={SourcePage} />
              <Route exact path="/promo-code" component={PromoCodePage} />
            </>
          )}
          {(role === UserRole.MANAGER ||
            role === UserRole.OPERATOR ||
            role === UserRole.HEAD_OPERATOR ||
            role === UserRole.PHARMACY_MANAGER) && (
            <Route exact path="/report" component={ReportPage} />
          )}
          {(role === UserRole.MANAGER ||
            role === UserRole.PHARMACIST ||
            role === UserRole.PHARMACY_MANAGER) && (
            <Route exact path="/test-orders" component={TestOrdersPage} />
          )}
          {role === UserRole.PHARMACIST &&
            (process.env.REACT_APP_RXGATE_ACTIVE === 'true' ? (
              <Route exact path="/rxgate" component={RXGatePage} />
            ) : (
              <Route
                exact
                path="/rxgate"
                render={() => <Redirect to="/orders" />}
              />
            ))}
          {role === UserRole.PHARMACIST &&
            process.env.REACT_APP_GLOVO_EDIT === 'true' && (
              <Route exact path="/settings" component={SettingsPharmacyPage} />
            )}
          {process.env.REACT_APP_LOYALTY_ENABLED === 'true' && (
            <Route
              exact
              path="/user-offline-address-screen"
              component={() => ClientLoyaltyPage({ updClientLoyaltyImg })}
            />
          )}
          {(role === UserRole.MANAGER ||
            role === UserRole.PHARMACY_MANAGER) && (
            <Route
              exact
              path="/pharmacist"
              component={OperatorsPharmacistPage}
            />
          )}
          {(role === UserRole.MANAGER ||
            role === UserRole.CONTENT ||
            role === UserRole.HEAD_OPERATOR ||
            role === UserRole.PHARMACY_MANAGER ||
            role === UserRole.OPERATOR) && (
            <Route exact path="/products" component={ProductsPage} />
          )}
          {(role === UserRole.MANAGER ||
            role === UserRole.CONTENT ||
            role === UserRole.HEAD_OPERATOR ||
            role === UserRole.PHARMACY_MANAGER ||
            role === UserRole.OPERATOR) && (
            <Route exact path="/edit" component={EditPage} />
          )}
          {role !== UserRole.CUSTOMER && role !== UserRole.CONTENT && (
            <Styled.OrderContainer>
              <Route exact path="/orders/:orderId" component={OrderPage} />
              <Route exact path="/test-orders/:orderId" component={OrderPage} />
            </Styled.OrderContainer>
          )}
        </Switch>
        {(window.location.pathname === '/login' ||
          window.location.pathname === '/') &&
          role !== UserRole.CUSTOMER &&
          role !== UserRole.CONTENT && <Redirect to="/orders" />}
      </Styled.MainPageContentContainer>
      {isShowLayout && <Footer />}
      {isShowLayout && <Controls />}
      <NotificationsList />
      {isShowLayout &&
        role === UserRole.PHARMACIST &&
        isSupportWidgetEnabled && (
          <SupportWidget defaultOpen={isSupportOpenRequested} />
        )}
    </>
  );
};

export default withAuth(MainPage);
